'use client'

import Link from 'next/link'
import {
  ArrowRight,
  Enterprise as Building2,
  Chip as Cpu,
  Cloud,
  Security as Shield,
  Location as MapPin,
  Group as Users,
  Badge as Award,
  Lightning as Zap,
  Target,
  Rocket,
  MachineLearning as Brain,
  Education as GraduationCap,
  CurrencyDollar as DollarSign,
  ChartColumn as BarChart3,
  Code as Code2,
  Growth as TrendingUp,
  Time as Clock,
  Headphones as HeadphonesIcon,
  Layers,
  AiGenerate as Sparkles,
  Locked as Lock,
  ChartPie as PieChart,
  BareMetalServer as Server,
  ShoppingCart,
  Industry as Factory,
  HealthCross as HeartPulse,
  Finance as Landmark,
  Meter as Gauge,
} from '@carbon/icons-react'
import { BookMeetingButton } from '@/components/book-meeting-button'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { siteConfig } from '@/config/site'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { PanelDots } from '@/components/fx/panel-dots'
import { MeshPanel } from '@/components/fx/mesh-panel'

interface CityLocationClientProps {
  citySlug: string
  cityData: any
  cityName: string
  region: string
  industries: string[]
}

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="text-[11px] font-black uppercase tracking-[0.24em] text-foreground">
    {children}
  </span>
)

// Industry icon mapping
const industryIcons: Record<string, any> = {
  Manufacturing: Factory,
  'IT & Technology': Server,
  Finance: Landmark,
  Education: GraduationCap,
  Healthcare: HeartPulse,
  Retail: ShoppingCart,
  Automotive: Gauge,
  Pharmaceuticals: Factory,
  Textiles: Layers,
  Chemicals: Factory,
}

export default function CityLocationClient({
  citySlug,
  cityData,
  cityName,
  region,
  industries,
}: CityLocationClientProps) {
  // Euclideum Services Data — copy unchanged from the previous version.
  const euclideumServices = [
    {
      icon: Building2,
      link: '/products/udc',
      title: 'Unified Digital Campus (UDC)',
      description:
        'Seamlessly integrate and unify campus data with powerful orchestration. Break down silos and enable real-time insights across your entire organization.',
      features: [
        'Data Orchestration',
        'Real-time Analytics',
        'Campus Integration',
        'API-First',
      ],
    },
    {
      icon: Users,
      title: 'HRMS',
      link: '/products/hrms',
      description:
        'Streamline workforce operations with comprehensive employee lifecycle management. From recruitment to retirement, manage every aspect of HR with AI-powered insights.',
      features: [
        'Employee Lifecycle',
        'Performance Management',
        'Strategic Planning',
        'Analytics',
      ],
    },
    {
      icon: DollarSign,
      title: 'Payroll Management',
      link: '/products/payroll',
      description:
        'Ensure accurate compensation with intelligent payroll processing and compliance. Handle complex calculations, tax laws, and multi-jurisdiction requirements seamlessly.',
      features: [
        'Auto Calculations',
        'Compliance Ready',
        'Tax Processing',
        'Multi-currency',
      ],
    },
    {
      icon: BarChart3,
      title: 'Accounts & Finance',
      link: '/products/accounts',
      description:
        'Modernize financial operations with automated processes, real-time insights, and intelligent forecasting. Strengthen financial governance and ensure regulatory compliance.',
      features: [
        'Auto Reconciliation',
        'Real-time Reporting',
        'Audit Trail',
        'Forecasting',
      ],
    },
    {
      icon: GraduationCap,
      title: 'LMS',
      link: '/products/lms',
      description:
        'Empower continuous learning with AI-powered personalized training experiences. Track progress, maximize knowledge retention, and build a culture of growth.',
      features: [
        'AI Learning',
        'Progress Tracking',
        'Skill Development',
        'Certifications',
      ],
    },
    {
      icon: Cloud,
      title: 'Cloud & DevOps',
      link: '/solutions/cloud-infrastructure',
      description:
        'Optimized infrastructure for speed, security, and reliability. Deploy and scale applications with confidence across multi-cloud environments with 99.9% uptime.',
      features: [
        'Multi-Cloud',
        'CI/CD Pipeline',
        '24/7 Monitoring',
        'Auto-scaling',
      ],
    },
    {
      icon: Brain,
      title: 'AI-Driven Solutions',
      link: '/solutions/ai-infrastructure',
      description:
        'Smart content generation, intelligent code review, productivity tools, and biometric systems. Leverage AI to automate complex tasks and gain competitive advantage.',
      features: [
        'Smart Automation',
        'Predictive Analytics',
        'NLP Processing',
        'Computer Vision',
      ],
    },
    {
      icon: Code2,
      title: 'Product Engineering',
      link: '/solutions/cloud-infrastructure',
      description:
        'End-to-end product development from ideation to launch. Build scalable, future-ready products with modern engineering practices and agile methodology.',
      features: [
        'Full-stack',
        'Quality Assurance',
        'Tech Documentation',
        'Agile Process',
      ],
    },
  ]

  // Benefits data — copy unchanged from the previous version.
  const benefits = [
    {
      icon: TrendingUp,
      title: 'Actionable Insights',
      description:
        'Leverage advanced analytics and AI-powered dashboards to make data-driven decisions. Our ERP provides real-time visibility into KPIs, trends, and opportunities.',
      metrics: ['Real-time Analytics', 'Predictive Modeling', 'Custom Reports'],
    },
    {
      icon: Sparkles,
      title: 'Extensive Customization',
      description:
        'Tailor every module to your unique business processes. Our low-code platform enables rapid customization and seamless integration with your existing tools.',
      metrics: ['Low-code Platform', 'API Integration', 'Workflow Builder'],
    },
    {
      icon: Lock,
      title: 'Financial Automation',
      description:
        'Automate complex financial workflows with intelligent rules and approvals. Reduce errors, ensure compliance, and close books faster with real-time reconciliation.',
      metrics: ['Auto-reconciliation', 'Compliance Checks', 'Audit Trail'],
    },
    {
      icon: Layers,
      title: 'Robust Operational Tools',
      description:
        'Streamline operations with integrated tools for inventory, procurement, and quality management. Scale your business without operational bottlenecks.',
      metrics: [
        'Inventory Control',
        'Quality Management',
        'Process Automation',
      ],
    },
  ]

  const whyChoose = [
    {
      icon: Cloud,
      title: 'Scalable Architecture',
      desc: 'Cloud-native infrastructure that grows with your business',
    },
    {
      icon: Users,
      title: 'Local Expertise',
      desc: 'Dedicated regional teams with deep industry knowledge',
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      desc: 'Bank-grade encryption and compliance standards',
    },
    {
      icon: Brain,
      title: 'AI-First Approach',
      desc: 'Intelligent automation and predictive analytics',
    },
    {
      icon: Target,
      title: 'End-to-End Ownership',
      desc: 'Complete project management from inception to delivery',
    },
    {
      icon: Award,
      title: 'Long-term Partnership',
      desc: 'Committed to your success with ongoing support',
    },
  ]

  const capabilities = [
    { icon: Cpu, title: 'AI Infrastructure', desc: 'Intelligent automation' },
    { icon: Cloud, title: 'Cloud & DevOps', desc: '99.9% uptime' },
    {
      icon: Building2,
      title: 'Enterprise ERP',
      desc: 'End-to-end integration',
    },
    { icon: Shield, title: 'Data Security', desc: 'Bank-grade encryption' },
  ]

  return (
    <main className="relative bg-background">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          /* Service, not Product: this page sells delivery of campus ERP in a
             city, and the node it used to emit put `address` and `provider` on
             a Product — neither is a property of Product — with an Offer whose
             PriceSpecification carried a currency and no price. Service takes
             provider and areaServed as declared properties, and no price is
             published anywhere in this repo to state. */
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: 'Campus ERP software implementation and support',
            name: `Euclideum ERP Solutions ${cityName}`,
            description: `Enterprise software solutions in ${cityName}`,
            areaServed: {
              '@type': 'City',
              name: cityName,
              containedInPlace: {
                '@type': 'AdministrativeArea',
                name: region,
                address: {
                  '@type': 'PostalAddress',
                  addressRegion: region,
                  addressCountry: 'IN',
                },
              },
            },
            /* Points at the Organization the root layout already ships on this
               page rather than restating it. The node here carried a different
               name and a different sameAs list from that one, so the two read
               as two companies. */
            provider: { '@id': `${siteConfig.url}/#organization` },
          }),
        }}
      />

      {/* ── Hero — a deck-style panel with the animated dot matrix ── */}
      <section className="container mx-auto pt-10 sm:pt-14">
        <div className="relative overflow-hidden rounded-xl p-7 sm:p-9 lg:p-12">
          <MeshPanel seedKey={`${citySlug}-hero`} />
          <PanelDots className="text-zinc-800 dark:text-white" />
          <div className="relative flex min-w-0 flex-col text-foreground">
            <Breadcrumbs
              className="mb-5 text-foreground/70"
              items={[
                { name: 'Home', href: '/' },
                { name: 'Service Locations', href: '/service-locations' },
                { name: cityName, href: `/service-locations/${citySlug}` },
              ]}
            />

            <div className="flex items-center gap-2 text-foreground/80">
              <MapPin className="size-4" />
              <span className="text-[11px] font-black uppercase tracking-[0.24em]">
                {cityName} • {region} • India
              </span>
            </div>

            <h1 className="font-heading mt-6 max-w-[24ch] text-3xl font-normal leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              ERP Software Company in {cityName}
            </h1>

            <p className="mt-4 max-w-[52ch] text-sm leading-relaxed text-foreground/80 sm:text-base">
              Empowering enterprises in {cityName} with intelligent ERP systems,
              AI-driven automation, and secure cloud infrastructure built for
              performance and growth.
            </p>

            <div className="flex flex-wrap gap-3 pt-8">
              {/* Opens the Cal booking modal. This used to link to
                  /about-us/team, which renders only a "Coming Soon" card, so
                  the primary CTA on all 18 city pages was a dead end. */}
              <BookMeetingButton size="xl">
                Connect with Our {cityName} Team
              </BookMeetingButton>
              <Button asChild variant="outline" size="xl">
                <Link href="#solutions">Explore Solutions</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why ERP + benefits ── */}
      <section className="container mx-auto pt-16 sm:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <Eyebrow>Enterprise Ready</Eyebrow>
          <h2 className="font-heading mt-4 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
            <ChromaText>
              Why Do Companies Need ERP Software in {cityName}?
            </ChromaText>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
            An ERP software company in {cityName} automates key business
            processes from accounts receivable, warehouse management, bank
            reconciliation, and lead management to financial management. It
            provides end-to-end capabilities to track expenses, generate
            invoices, and identify project profitability, all without relying on
            several disconnected systems.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-12 sm:gap-5 md:grid-cols-2 md:gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="flex flex-col rounded-xl border border-border/70 bg-background p-6 md:p-7"
            >
              <benefit.icon className="size-7 text-emerald-600 dark:text-primary-active" />
              <h3 className="mt-4 text-lg font-semibold text-foreground">
                {benefit.title}
              </h3>
              <p className="mt-2 text-sm leading-[1.7] text-muted-foreground">
                {benefit.description}
              </p>
              <div className="mt-auto flex flex-wrap gap-1.5 pt-4 md:gap-2">
                {benefit.metrics.map((metric) => (
                  <span
                    key={metric}
                    className="rounded-[4px] bg-emerald-600/10 px-2.5 py-1 text-[11px] font-medium text-foreground dark:bg-primary-active/10"
                  >
                    {metric}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Enterprise suite ── */}
      <section
        id="solutions"
        className="container mx-auto scroll-mt-20 pt-16 sm:pt-24"
      >
        <div className="mx-auto max-w-3xl text-center">
          <Eyebrow>Award-Winning Solutions</Eyebrow>
          <h2 className="font-heading mt-4 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
            <ChromaText>Our Enterprise Suite for {cityName}</ChromaText>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
            Comprehensive, integrated solutions designed to transform your
            business operations in {cityName}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:mt-12 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4 md:gap-6">
          {euclideumServices.map((service) => (
            <Link
              key={service.title}
              href={service.link}
              className="group flex h-full flex-col rounded-xl border border-border/70 bg-background p-6"
            >
              <service.icon className="size-7 text-emerald-600 dark:text-primary-active" />
              <h3 className="mt-4 text-base font-semibold leading-snug text-foreground">
                {service.title}
              </h3>
              <p className="mt-2 text-[13px] leading-[1.7] text-muted-foreground">
                {service.description}
              </p>
              <div className="mt-auto flex flex-wrap gap-1.5 pt-4">
                {service.features.slice(0, 3).map((feature) => (
                  <span
                    key={feature}
                    className="rounded-[4px] bg-emerald-600/10 px-2 py-0.5 text-[10px] font-medium text-foreground dark:bg-primary-active/10"
                  >
                    {feature}
                  </span>
                ))}
              </div>
              <span className="inline-flex w-fit items-center gap-1.5 pt-5 text-[13px] font-medium text-foreground">
                Learn More
                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>

        {/* Support band — deep panel + dot matrix again */}
        <div className="relative mt-10 overflow-hidden rounded-xl p-7 sm:mt-12 sm:p-9 lg:p-10">
          <MeshPanel seedKey={`${citySlug}-support`} />
          <PanelDots className="text-zinc-800 dark:text-white" />
          <div className="relative flex flex-col items-start justify-between gap-6 text-foreground lg:flex-row lg:items-center">
            <div>
              <div className="flex items-center gap-2 text-foreground/80">
                <HeadphonesIcon className="size-4" />
                <span className="text-[11px] font-black uppercase tracking-[0.24em]">
                  Dedicated Support
                </span>
              </div>
              <h3 className="font-heading mt-3 text-2xl font-normal tracking-tight lg:text-3xl">
                Need Help Getting Started in {cityName}?
              </h3>
              {/* "available around the clock" contradicted /support, which
                  publishes Monday-Friday, 9:00-6:00 IST. */}
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground/80 sm:text-base">
                Our {cityName} support team is on hand Monday to Friday, 9:00 AM
                to 6:00 PM IST, to assist with onboarding, troubleshooting, and
                everything in between.
              </p>
            </div>
            <Button asChild size="xl" className="shrink-0">
              <Link href="/support">Contact Support</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section className="container mx-auto pt-16 sm:pt-24">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <Eyebrow>Digital Transformation</Eyebrow>
            <h2 className="font-heading mt-4 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
              <ChromaText>
                Driving Digital Transformation in {cityName}
              </ChromaText>
            </h2>
            <p className="mt-5 text-sm leading-[1.8] text-muted-foreground sm:text-base">
              Euclideum's presence in {cityName} supports enterprises across{' '}
              {industries.slice(0, 4).join(', ')}, and{' '}
              {industries[4] || 'service'} sectors. We deliver enterprise-grade
              architecture with scalable cloud-native systems.
            </p>
            <p className="mt-3 text-sm leading-[1.8] text-muted-foreground/80 sm:text-base">
              From ERP modernization to AI-driven intelligence, our regional
              experts collaborate to drive measurable business outcomes.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 md:gap-5">
            {capabilities.map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-border/70 bg-background p-6"
              >
                <item.icon className="size-6 text-emerald-600 dark:text-primary-active" />
                <h3 className="mt-3 text-sm font-semibold text-foreground sm:text-base">
                  {item.title}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground sm:text-[13px]">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="container mx-auto pt-16 sm:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <Eyebrow>Industry Expertise</Eyebrow>
          <h2 className="font-heading mt-4 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
            <ChromaText>Industries We Serve in {cityName}</ChromaText>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
            Specialized ERP solutions for {cityName}'s key industrial sectors,
            tailored to meet unique business requirements
          </p>
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-3 sm:gap-4">
          {industries.map((industry) => {
            const Icon = industryIcons[industry] || Building2
            return (
              <div
                key={industry}
                className="flex items-center gap-3 rounded-xl bg-muted px-5 py-3.5 dark:bg-muted/60"
              >
                <Icon className="size-5 text-emerald-600 dark:text-primary-active" />
                <span className="text-sm font-medium text-foreground">
                  {industry}
                </span>
              </div>
            )
          })}
        </div>
      </section>

      {/* ── Why choose us ── */}
      <section className="container mx-auto pt-16 sm:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <Eyebrow>Why Partner With Us</Eyebrow>
          <h2 className="font-heading mt-4 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
            <ChromaText>Why Choose Euclideum in {cityName}</ChromaText>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
            What makes us the preferred technology partner for enterprises in{' '}
            {cityName}
          </p>
        </div>

        <div className="mt-10 grid gap-x-8 gap-y-7 text-left sm:mt-12 md:grid-cols-3">
          {whyChoose.map((item) => (
            <div key={item.title} className="flex gap-4">
              <item.icon className="mt-0.5 size-6 shrink-0 text-emerald-600 dark:text-primary-active" />
              <div>
                <h3 className="mb-1 font-semibold text-foreground">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Contact ── */}
      {cityData?.contactInfo && (
        <section className="container mx-auto pt-16 sm:pt-24">
          <div className="rounded-xl border border-border/70 bg-background p-7 sm:p-9 lg:p-12">
            <div className="mb-8 text-center">
              <Eyebrow>Visit Us</Eyebrow>
              <h2 className="font-heading mt-3 text-2xl font-normal tracking-tight text-foreground sm:text-3xl">
                Our {cityName} Office
              </h2>
              <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                Visit us or get in touch with our local team
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-primary-active" />
                  <div>
                    <h3 className="mb-1 font-semibold text-foreground">
                      Address
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {cityData.contactInfo.address}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Users className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-primary-active" />
                  <div>
                    <h3 className="mb-1 font-semibold text-foreground">
                      Email
                    </h3>
                    <a
                      href={`mailto:${cityData.contactInfo.email}`}
                      className="text-sm text-muted-foreground transition-colors hover:text-emerald-600 dark:hover:text-primary-active"
                    >
                      {cityData.contactInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <HeadphonesIcon className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-primary-active" />
                  <div>
                    <h3 className="mb-1 font-semibold text-foreground">
                      Phone
                    </h3>
                    <a
                      href={`tel:${cityData.contactInfo.phone}`}
                      className="text-sm text-muted-foreground transition-colors hover:text-emerald-600 dark:hover:text-primary-active"
                    >
                      {cityData.contactInfo.phone}
                    </a>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Clock className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-primary-active" />
                  <div className="flex-1">
                    <h3 className="mb-3 font-semibold text-foreground">
                      Office Hours
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-muted-foreground">
                        <span>Monday - Saturday</span>
                        <span className="font-medium">11:00 AM - 7:00 PM</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Sunday</span>
                        <span className="font-medium text-emerald-600 dark:text-primary-active">
                          Closed
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <Button
                    className="group w-full bg-emerald-600 text-primary-foreground hover:bg-emerald-600/90 dark:bg-primary-active dark:hover:bg-primary-active/90"
                    asChild
                  >
                    <Link href="/schedule-meeting">
                      Schedule a Visit
                      <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="container mx-auto pb-16 pt-16 sm:pb-24 sm:pt-24">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <Rocket className="size-4 text-emerald-600 dark:text-primary-active" />
            <Eyebrow>Get Started Today</Eyebrow>
          </div>
          <h2 className="font-heading text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl md:text-5xl">
            <ChromaText>
              Ready to Transform Your Business in {cityName}?
            </ChromaText>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base md:text-[17px]">
            Partner with Euclideum to build intelligent, scalable, and
            future-ready enterprise systems tailored for your growth.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              size="lg"
              className="group bg-emerald-600 font-medium text-primary-foreground hover:bg-emerald-600/90 dark:bg-primary-active dark:hover:bg-primary-active/90"
              asChild
            >
              <Link href="/schedule-meeting">
                Schedule a Consultation
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link href="/products">
                <Layers className="mr-2 size-4" />
                Explore Products
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  )
}
